<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import type { HTMLAttributes } from 'vue'
import { computed, nextTick, ref, useAttrs, watch } from 'vue'

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    /** Horaire au format HH:mm (24 h), ou la saisie brute si elle n'est pas reconnue. */
    modelValue?: string | null
    /** Intervalle en minutes entre deux créneaux de la liste. */
    step?: number
    placeholder?: string
    disabled?: boolean
    class?: HTMLAttributes['class']
  }>(),
  { modelValue: undefined, step: 30, placeholder: undefined, disabled: false, class: undefined },
)

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | undefined): void
}>()

// vee-validate (componentField) branche aussi onChange/onInput natifs : ils
// reliraient la saisie brute (« 1230 ») par-dessus la valeur normalisee
const attrs = useAttrs()
const inputAttrsRef = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'onChange' && key !== 'onInput')),
)

const pad = (n: number) => String(n).padStart(2, '0')

/** Reconnaît « 9 », « 930 », « 0930 », « 9:30 », « 9h30 », « 9.30 » ; null sinon. */
function parseTime(text: string): string | null {
  const match = text.trim().match(/^(\d{1,2})(?:\s*[:hH.]\s*|(?=\d{2}$))?(\d{2})?$/)
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2] ?? 0)
  if (hour > 23 || minute > 59) return null
  return `${pad(hour)}:${pad(minute)}`
}

const slots = computed(() =>
  Array.from({ length: Math.ceil(1440 / props.step) }, (_, i) => {
    const minutes = i * props.step
    return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
  }),
)

const textRef = ref(props.modelValue ?? '')
const isOpenRef = ref(false)
const highlightedRef = ref(-1)
const listRef = ref<HTMLElement>()
const anchorRef = ref<HTMLElement>()

// un clic dans le champ lui-meme ne doit pas refermer la liste
function onInteractOutside(event: Event) {
  if (anchorRef.value?.contains(event.target as Node)) event.preventDefault()
}

watch(
  () => props.modelValue,
  (value) => {
    if ((parseTime(textRef.value) ?? textRef.value) !== (value ?? '')) textRef.value = value ?? ''
  },
)

/** Insère « : » pendant la frappe de chiffres seuls : « 1230 » → « 12:30 », « 930 » → « 9:30 ». */
function withColon(digits: string): string {
  const hourLength = digits[0] > '2' ? 1 : 2
  if (digits.length <= hourLength) return digits
  return `${digits.slice(0, hourLength)}:${digits.slice(hourLength, hourLength + 2)}`
}

function onInput(event: Event) {
  const input = event.target as HTMLInputElement
  const isTyping = (event as InputEvent).inputType?.startsWith('insert')
  if (isTyping && /^\d+$/.test(input.value)) input.value = withColon(input.value)
  textRef.value = input.value
  highlightedRef.value = -1
  emits('update:modelValue', textRef.value ? (parseTime(textRef.value) ?? textRef.value) : undefined)
}

function onBlur() {
  const parsed = parseTime(textRef.value)
  if (parsed) textRef.value = parsed
  isOpenRef.value = false
}

function select(slot: string) {
  textRef.value = slot
  emits('update:modelValue', slot)
  isOpenRef.value = false
}

function open() {
  if (props.disabled || isOpenRef.value) return
  isOpenRef.value = true
}

// comme Teams : la liste s'ouvre sur le creneau courant (ou le plus proche)
function nearestSlotIndex() {
  const value = parseTime(textRef.value)
  if (!value) return slots.value.indexOf('09:00')
  const index = slots.value.findIndex((slot) => slot >= value)
  return index === -1 ? slots.value.length - 1 : index
}

watch(isOpenRef, async (isOpen) => {
  if (!isOpen) return
  const index = nearestSlotIndex()
  highlightedRef.value = parseTime(textRef.value) === slots.value[index] ? index : -1
  await nextTick()
  listRef.value?.children[index]?.scrollIntoView({ block: 'center' })
})

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    if (!isOpenRef.value) return open()
    const delta = event.key === 'ArrowDown' ? 1 : -1
    const start = highlightedRef.value === -1 ? nearestSlotIndex() - (delta > 0 ? 1 : 0) : highlightedRef.value
    highlightedRef.value = Math.min(Math.max(start + delta, 0), slots.value.length - 1)
    listRef.value?.children[highlightedRef.value]?.scrollIntoView({ block: 'nearest' })
  } else if (event.key === 'Enter' && isOpenRef.value) {
    event.preventDefault()
    if (highlightedRef.value !== -1) select(slots.value[highlightedRef.value])
    else onBlur()
  } else if (event.key === 'Escape' && isOpenRef.value) {
    event.stopPropagation()
    isOpenRef.value = false
  }
}

const listboxId = `time-picker-${Math.random().toString(36).slice(2, 8)}`
</script>

<template>
  <Popover :open="isOpenRef" @update:open="(v) => (isOpenRef = v)">
    <PopoverAnchor as-child>
      <div ref="anchorRef" :class="cn('relative w-full', props.class)">
        <input
          v-bind="inputAttrsRef"
          :value="textRef"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="none"
          :aria-expanded="isOpenRef"
          :aria-controls="listboxId"
          :aria-activedescendant="highlightedRef !== -1 ? `${listboxId}-${highlightedRef}` : undefined"
          :placeholder="placeholder"
          :disabled="disabled"
          data-slot="time-picker"
          class="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent py-1 pr-9 pl-3 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
          @input="onInput"
          @click="open"
          @focus="open"
          @blur="onBlur"
          @keydown="onKeydown"
        />
        <ChevronDown
          class="text-muted-foreground pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 opacity-50"
          aria-hidden="true"
        />
      </div>
    </PopoverAnchor>
    <PopoverContent
      align="start"
      class="w-(--reka-popover-trigger-width) p-1"
      @open-auto-focus.prevent
      @close-auto-focus.prevent
      @interact-outside="onInteractOutside"
    >
      <div :id="listboxId" ref="listRef" role="listbox" class="max-h-60 overflow-y-auto">
        <div
          v-for="(slot, index) in slots"
          :id="`${listboxId}-${index}`"
          :key="slot"
          role="option"
          :aria-selected="slot === modelValue"
          :data-highlighted="index === highlightedRef ? '' : undefined"
          class="hover:bg-accent data-[highlighted]:bg-accent aria-selected:bg-primary/10 aria-selected:text-primary cursor-pointer rounded-sm px-3 py-1.5 text-sm tabular-nums"
          @mousedown.prevent="select(slot)"
        >
          {{ slot }}
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
